import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {WidthItem} from 'src/components/width-item';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {CustomValidatedControl} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {PresetEntitlementsTable, PresetSuggestSection} from 'src/modules/manage-entitlements/presets-entitlements';
import {useAssignGrantsToPreset} from 'src/modules/manage-entitlements/presets-entitlements/useAssignGrantsToPreset';
import {useAssignRolesToPreset} from 'src/modules/manage-entitlements/presets-entitlements/useAssignRolesToPreset';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {ManageEntitlementsPage} from '../components/manage-entitlements-page';

import {PRESET_NAME_CONTROL} from './constants';
import {useCreatePreset} from './useCreatePreset';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {PresetRelation} from 'src/openapi/admin/models/PresetRelation';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';


const CreatePreset = () => {
    const navigate = useNavigate();
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {proceed, setIsChunkChanged} = useConfirmationChunk({chunkName: 'create-preset', onProceed: () => {
        setSuccessMessage('Preset has been successfully created.');
        navigate('/entitlements/presets');
    }});

    const [busy, setBusy] = useState(false);

    const {
        data,
        setData,
        isChanged: isPresetChanged,
        saveChanges: createPreset,
        validateChanges: validatePresetFields,
    } = useCreatePreset();

    const [relations, setRelations] = useState<PresetRelationItem[]>([]);

    const rolesRelations = useMemo<PresetRelation[]>(() => (relations
        .filter(({roleId, level}) => roleId && level)
        .map(({roleId, level}) => ({entityId: roleId || '', relationLevel: level}))
        .reduce((acc, {entityId, relationLevel}) => {
            const existed = acc.find(({entityId: id}) => id === entityId);
            if (existed) {
                return acc;
            }

            return [...acc, {entityId, relationLevel}];
        }, [] as PresetRelation[])
    ), [relations]);

    const grantsRelations = useMemo<PresetRelation[]>(() => (relations
        .filter(({roleId, level, grantId}) => !roleId && grantId && level)
        .map(({grantId, level}) => ({entityId: grantId, relationLevel: level}))
        .reduce((acc, {entityId, relationLevel}) => {
            const existed = acc.find(({entityId: id}) => id === entityId);
            if (existed) {
                return acc;
            }

            return [...acc, {entityId, relationLevel}];
        }, [] as PresetRelation[])
    ), [relations]);

    const {event: assignGrantsToPreset} = useAssignGrantsToPreset(grantsRelations);
    const {event: assignRolesToPreset} = useAssignRolesToPreset(rolesRelations);

    useEffect(() => {
        setIsChunkChanged(isPresetChanged);
    }, [isPresetChanged, setIsChunkChanged]);

    const changeName = useCallback((name: string) => {
        setData(prev => ({...prev, name}));
    }, [setData]);

    const handleDescriptionChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.target.value}));
    }, [setData]);

    const handleDescriptionBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        const trimmed = event.currentTarget.value.trim();
        setData(prev => ({...prev, description: trimmed}));
    }, [setData]);

    const backToPresets = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const createPresetHandler = useCallback(async () => {
        const error = validatePresetFields();

        if (error) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            return;
        }

        setBusy(true);
        const result = await createPreset();
        if (typeof result === 'object' && result.externalId) {
            if (rolesRelations.length) {
                const assignError = await assignRolesToPreset({presetId: result.externalId});
                if (assignError) {
                    setErrorMessage(assignError);
                    setBusy(false);
                    return;
                }
            }

            if (grantsRelations.length) {
                const assignError = await assignGrantsToPreset({presetId: result.externalId});
                if (assignError) {
                    setErrorMessage(assignError);
                    setBusy(false);
                    return;
                }
            }

            proceed();
        } else if (result) {
            setErrorMessage(result as string);
        }
        setBusy(false);
    }, [
        createPreset,
        rolesRelations,
        grantsRelations,
        assignRolesToPreset,
        assignGrantsToPreset,
        proceed,
        setErrorMessage,
        validatePresetFields,
    ]);

    const addRelations = useCallback((existed: PresetRelationItem[]) => {
        setRelations(prev => [...new Set([...prev, ...existed])]);
    }, []);

    return (
        <ServerResponseHandler
            serverError={null}
            accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
            sectionName="Create Preset">
            <ManageEntitlementsPage
                title="Create Preset"
                buttonsBlock={(
                    <Group>
                        <Button variant="Type.PRIMARY" busy={busy} onClick={createPresetHandler}>
                            Create
                        </Button>
                        <Button variant="Type.SECONDARY" onClick={backToPresets}>
                            Cancel
                        </Button>
                    </Group>
                )}>
                <Item className="group-item pd-l">
                    <FormSection headline="Preset Information">
                        <Group direction="v">
                            <CustomValidatedControl name={PRESET_NAME_CONTROL}>
                                {error => (
                                    <FormInput
                                        value={data.name}
                                        width={300}
                                        maxLength={64}
                                        label={<RequiredMark label="Preset Name"/>}
                                        placeholder="Preset Name"
                                        onBlur={changeName}
                                        valid={error ? false : undefined}
                                        errorDescription={error}
                                    />
                                )}
                            </CustomValidatedControl>
                            <WidthItem width={610}>
                                <Label>Description</Label>
                                <Textarea
                                    maxLength={255}
                                    value={data.description}
                                    onBlur={handleDescriptionBlur}
                                    onChange={handleDescriptionChange}
                                />
                            </WidthItem>
                        </Group>
                    </FormSection>
                    <PresetSuggestSection
                        relations={relations}
                        onAddRelations={addRelations}
                    />
                </Item>
                <PresetEntitlementsTable
                    relations={relations}
                    onChange={setRelations}
                />
            </ManageEntitlementsPage>
        </ServerResponseHandler>
    );
};

export const ValidateCreatePreset = () => (
    <ValidationContextProvider>
        <CreatePreset/>
    </ValidationContextProvider>
);
